import React, { useState } from 'react'
import Icon from 'components/elements/Icon'

import { useAppSelector } from 'redux/hooks'
import { selectUsedCategories } from 'redux/deck/selectors'

import { CategoryType } from 'types/deck'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import SimpleSpinner from 'components/elements/SimpleSpinner'

import styles from './categoryTable.module.scss'

type Props = {
  categories: CategoryType[]
  personalCategoriesPage?: boolean
} & (
  | {
      onEdit: (category: CategoryType) => void
      onDelete: (category: CategoryType) => void
      onTemplateAdd?: never
    }
  | {
      onTemplateAdd: (template: CategoryType) => Promise<void>
      onEdit?: never
      onDelete?: never
    }
)

const CategoryTable = ({ categories, onEdit, onDelete, onTemplateAdd, personalCategoriesPage = false }: Props) => {
  const usedCategories = useAppSelector(selectUsedCategories)

  const [templateLoading, setTemplateLoading] = useState<Record<string, boolean>>({})

  const handleAddTemplate = (category: CategoryType) => {
    if (!onTemplateAdd) return

    setTemplateLoading({ ...templateLoading, [category.name]: true })

    return onTemplateAdd(category).then(() => setTemplateLoading({ ...templateLoading, [category.name]: false }))
  }

  const anyTemplateLoading = Object.values(templateLoading).some(loading => loading)

  return (
    <table className={styles.list}>
      <tbody>
        <tr>
          <th>Name</th>
          {!personalCategoriesPage && (
            <>
              <th className={styles.checkBox}>In Deck</th>
              <th className={styles.checkBox}>In Price</th>
              <th className={styles.checkBox}>Premier</th>
            </>
          )}
          <th className={styles.controls}></th>
        </tr>

        {categories.map(category => (
          <tr key={`${category.name}-${category.id}`}>
            <td>{category.name}</td>
            {!personalCategoriesPage && (
              <>
                <td className={styles.checkBox}>{category.includedInDeck && <Icon name="check circle" />}</td>
                <td className={styles.checkBox}>{category.includedInPrice && <Icon name="check circle" />}</td>
                <td className={styles.checkBox}>{category.isPremier && <Icon name="check circle" />}</td>
              </>
            )}
            <td className={styles.controls}>
              {onEdit && onDelete && (
                <ArchidektDropdown
                  options={[
                    {
                      label: 'Remove',
                      icon: 'trash',
                      onClick: () => onDelete(category),
                      disabled: usedCategories[category.name],
                    },
                    {
                      label: 'Edit',
                      icon: 'edit',
                      onClick: () => onEdit(category),
                    },
                  ]}
                />
              )}

              {onTemplateAdd && (
                <button
                  title="Add template to deck"
                  className={styles.addButton}
                  onClick={() => handleAddTemplate(category)}
                  disabled={anyTemplateLoading}>
                  {templateLoading[category.name] ? <SimpleSpinner size="xSmall" /> : <Icon name="add" />}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default CategoryTable
